import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../../content';

const Question = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getC, loaded: contentLoaded } = useContent(['beyond']);
  const currentQIdx = parseInt(id);

  // Initialize state from local storage or empty
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem('beyondTestAnswers');
    return saved ? JSON.parse(saved) : {};
  });

  const question = getC(`beyond.beyond_test.questions.${currentQIdx}`);

  useEffect(() => {
    localStorage.setItem('beyondTestAnswers', JSON.stringify(answers));
  }, [answers]);

  const shuffledOptions = React.useMemo(() => {
    if (!question?.a) return [];
    const options = Object.keys(question.a);
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    return options;
  }, [currentQIdx, question]);

  if (!contentLoaded || !question) {
    return <div />;
  }

  const handleAnswer = (choice) => {
    const newAnswers = { ...answers, [currentQIdx]: choice };
    setAnswers(newAnswers);

    if (currentQIdx < 15) {
      navigate(`/beyond-test/q/${currentQIdx + 1}`);
    } else {
      navigate('/beyond-test/loading');
    }
  };

  const progress = (currentQIdx / 15) * 100;

  return (
    <div className="beyond-test-container">
      <div className="question-card">
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          <span className="progress-text">{currentQIdx} / 15</span>
        </div>

        <h2 className="question-text">{question.q}</h2>

        <div className="options-container">
          {shuffledOptions.map((opt, index) => (
            <button key={opt} className="option-btn" onClick={() => handleAnswer(opt)}>
              {/* Hide the A/B/C/D label since it's shuffled and might be confusing, or keep it but it won't be alphabetical */}
              {/* User asked to shuffle because 1,2,3,4 (A,B,C,D) patterns were too obvious. 
                                Providing a neutral index (1,2,3,4) visually might be better than showing A,B,C,D if A is always "S-A". 
                                But the logic needs 'opt' (A,B,C,D). 
                                Let's just show the text, or use a neutral bullet point. */}
              <span className="option-label">{String.fromCharCode(65 + index)}</span>
              {question.a[opt]}
            </button>
          ))}
        </div>

        {currentQIdx > 1 && (
          <button className="back-btn" onClick={() => navigate(`/beyond-test/q/${currentQIdx - 1}`)}>
            Back
          </button>
        )}
      </div>

      <style jsx>{`
        .beyond-test-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: linear-gradient(135deg, #0f172a 0%, #312e81 100%);
          color: white;
        }

        .question-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 2rem 1.5rem;
          width: 100%;
          max-width: 500px;
          min-height: 60vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }

        .progress-container {
          margin-bottom: 3rem;
          position: relative;
        }

        .progress-bar {
          height: 6px;
          background: linear-gradient(to right, #8b5cf6, #ec4899);
          border-radius: 10px;
          transition: width 0.3s ease;
        }

        .progress-text {
          position: absolute;
          right: 0;
          top: -25px;
          font-size: 0.9rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .question-text {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.4;
          margin-bottom: 3rem;
          word-break: keep-all;
          text-align: center;
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .options-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .option-btn {
          width: 100%;
          padding: 1.25rem;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          color: white;
          font-size: 1.1rem;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
        }

        .option-btn:hover {
          background: rgba(139, 92, 246, 0.2);
          border-color: #8b5cf6;
          transform: translateY(-2px);
        }

        .option-label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          font-size: 0.8rem;
          font-weight: bold;
          margin-right: 12px;
          flex-shrink: 0;
        }

        .back-btn {
            margin-top: 1.5rem;
            background: transparent;
            border: none;
            color: #64748b;
            cursor: pointer;
            font-size: 0.9rem;
            align-self: center;
            padding: 0.5rem;
        }
        
        .back-btn:hover {
            color: #94a3b8;
            text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default Question;
